import type { RatingDailyRecord } from '../types/styling-analysis.js';
import type { Granularity } from './kpi-aggregation.js';
import { periodKey } from './kpi-aggregation.js';

/** @graph-connects none */
export type SegmentFilter = 'all' | 'first' | 'repeat';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business Chart data point for rating analysis with computed good rate.
 * @graph-connects none
 */
export interface RatingChartPoint {
  label: string;
  totalOrders: number;
  ratingRespondedOrders: number;
  avgRating: number | null;
  avgDesignRating: number | null;
  avgColorRating: number | null;
  stylingReviewRespondedOrders: number;
  goodCount: number;
  badCount: number;
  goodRate: number | null;
}

/** @graph-connects none */
function toChartPoint(label: string, r: RatingDailyRecord): RatingChartPoint {
  const reviewed = r.goodCount + r.badCount;
  const avgRating =
    r.avgDesignRating != null && r.avgColorRating != null
      ? Math.round(((r.avgDesignRating + r.avgColorRating) / 2) * 100) / 100
      : null;
  return {
    label,
    totalOrders: r.totalOrders,
    ratingRespondedOrders: r.ratingRespondedOrders,
    avgRating,
    avgDesignRating: r.avgDesignRating,
    avgColorRating: r.avgColorRating,
    stylingReviewRespondedOrders: r.stylingReviewRespondedOrders,
    goodCount: r.goodCount,
    badCount: r.badCount,
    goodRate: reviewed > 0 ? Math.round((r.goodCount / reviewed) * 1000) / 10 : null,
  };
}

/** @graph-connects none */
function aggregateBucket(label: string, rows: RatingDailyRecord[]): RatingChartPoint {
  const totalOrders = rows.reduce((s, r) => s + r.totalOrders, 0);
  const ratingRespondedOrders = rows.reduce((s, r) => s + r.ratingRespondedOrders, 0);
  const stylingReviewRespondedOrders = rows.reduce((s, r) => s + r.stylingReviewRespondedOrders, 0);
  const goodCount = rows.reduce((s, r) => s + r.goodCount, 0);
  const badCount = rows.reduce((s, r) => s + r.badCount, 0);

  const designSum = rows.reduce(
    (s, r) => s + (r.avgDesignRating != null ? r.avgDesignRating * r.ratingRespondedOrders : 0),
    0,
  );
  const colorSum = rows.reduce(
    (s, r) => s + (r.avgColorRating != null ? r.avgColorRating * r.ratingRespondedOrders : 0),
    0,
  );
  const avgDesign =
    ratingRespondedOrders > 0 ? Math.round((designSum / ratingRespondedOrders) * 100) / 100 : null;
  const avgColor =
    ratingRespondedOrders > 0 ? Math.round((colorSum / ratingRespondedOrders) * 100) / 100 : null;
  const avgRating =
    avgDesign != null && avgColor != null
      ? Math.round(((avgDesign + avgColor) / 2) * 100) / 100
      : null;
  const reviewed = goodCount + badCount;

  return {
    label,
    totalOrders,
    ratingRespondedOrders,
    avgRating,
    avgDesignRating: avgDesign,
    avgColorRating: avgColor,
    stylingReviewRespondedOrders,
    goodCount,
    badCount,
    goodRate: reviewed > 0 ? Math.round((goodCount / reviewed) * 1000) / 10 : null,
  };
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business Aggregate daily rating data by granularity and segment filter. Computes weighted averages for rating values and good rate.
 * @graph-connects periodKey [calls] Convert date to period bucket key
 */
export function aggregateRatingData(
  records: RatingDailyRecord[],
  granularity: Granularity,
  segment: SegmentFilter,
): RatingChartPoint[] {
  let filtered = records;
  if (segment === 'first') filtered = records.filter((r) => r.isFirstOrder);
  else if (segment === 'repeat') filtered = records.filter((r) => !r.isFirstOrder);
  else {
    const grouped = new Map<string, RatingDailyRecord>();
    for (const r of records) {
      const existing = grouped.get(r.date);
      if (existing) {
        const prevResp = existing.ratingRespondedOrders;
        const currResp = r.ratingRespondedOrders;
        const totalResp = prevResp + currResp;
        existing.totalOrders += r.totalOrders;
        existing.ratingRespondedOrders = totalResp;
        existing.stylingReviewRespondedOrders += r.stylingReviewRespondedOrders;
        existing.goodCount += r.goodCount;
        existing.badCount += r.badCount;
        existing.avgDesignRating =
          totalResp > 0
            ? ((existing.avgDesignRating ?? 0) * prevResp + (r.avgDesignRating ?? 0) * currResp) /
              totalResp
            : null;
        existing.avgColorRating =
          totalResp > 0
            ? ((existing.avgColorRating ?? 0) * prevResp + (r.avgColorRating ?? 0) * currResp) /
              totalResp
            : null;
        existing.avgRating =
          existing.avgDesignRating != null && existing.avgColorRating != null
            ? (existing.avgDesignRating + existing.avgColorRating) / 2
            : null;
      } else {
        grouped.set(r.date, { ...r });
      }
    }
    filtered = Array.from(grouped.values());
  }

  const sorted = [...filtered].sort((a, b) => (a.date < b.date ? -1 : 1));

  if (granularity === 'daily') {
    return sorted.map((r) => toChartPoint(r.date.slice(5), r));
  }

  const buckets = new Map<string, RatingDailyRecord[]>();
  for (const row of sorted) {
    const key = periodKey(row.date, granularity);
    const arr = buckets.get(key);
    if (arr) arr.push(row);
    else buckets.set(key, [row]);
  }

  const result: RatingChartPoint[] = [];
  for (const [key, rows] of buckets) {
    result.push(aggregateBucket(key, rows));
  }
  return result;
}
